import supabase from "@/services/supabaseClient";

export const ADMIN_PROFILE_BUCKET = "admin-profile-images";
export const ADMIN_PROFILE_UPDATED_EVENT = "afrimed:admin-profile-updated";
export const ADMIN_THEME_OPTIONS = ["system", "light", "dark"];
const ADMIN_SIGNIN_NAME_KEY = "afrimed:admin_signin_full_name";

function safeText(value) {
  return String(value || "").trim();
}

function fileExtensionFor(file) {
  const namePart = safeText(file?.name).split(".").pop();
  const ext = safeText(namePart).toLowerCase();
  if (ext) return ext;
  if (safeText(file?.type) === "image/png") return "png";
  if (safeText(file?.type) === "image/webp") return "webp";
  return "jpg";
}

export function readPendingAdminName() {
  if (typeof window === "undefined") return "";
  try {
    return safeText(window.localStorage.getItem(ADMIN_SIGNIN_NAME_KEY));
  } catch {
    return "";
  }
}

export function writePendingAdminName(value) {
  if (typeof window === "undefined") return;
  try {
    const next = safeText(value);
    if (next) window.localStorage.setItem(ADMIN_SIGNIN_NAME_KEY, next);
  } catch {
    // ignore
  }
}

export function clearPendingAdminName() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ADMIN_SIGNIN_NAME_KEY);
  } catch {
    // ignore
  }
}

function normalizeThemePreference(value) {
  const next = safeText(value).toLowerCase();
  return ADMIN_THEME_OPTIONS.includes(next) ? next : "";
}

export function broadcastAdminProfileUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ADMIN_PROFILE_UPDATED_EVENT));
}

export async function ensureAdminProfile({ userId, email, fullName } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Admin account is missing a user id.");

  const normalizedEmail = safeText(email).toLowerCase();
  const normalizedName = safeText(fullName) || readPendingAdminName();

  const { data: existing, error: existingError } = await supabase
    .from("admin_users")
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .eq("user_id", normalizedUserId)
    .maybeSingle();

  if (existingError) throw existingError;

  if (!existing) {
    const payload = {
      user_id: normalizedUserId,
      email: normalizedEmail,
      full_name: normalizedName || null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from("admin_users")
      .insert(payload)
      .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
      .single();
    if (error) throw error;
    if (safeText(data?.full_name)) clearPendingAdminName();
    return data;
  }

  const patch = {};
  if (normalizedEmail && normalizedEmail !== safeText(existing.email).toLowerCase()) {
    patch.email = normalizedEmail;
  }
  if (normalizedName && !safeText(existing.full_name)) {
    patch.full_name = normalizedName;
  }

  if (!Object.keys(patch).length) return existing;

  patch.updated_at = new Date().toISOString();
  const { data, error } = await supabase
    .from("admin_users")
    .update(patch)
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .single();

  if (error) throw error;
  if (safeText(data?.full_name)) clearPendingAdminName();
  return data;
}

export async function getAdminProfile({ userId, email, fullName } = {}) {
  return ensureAdminProfile({ userId, email, fullName });
}

export async function updateAdminProfile({ userId, fullName } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Admin account is missing a user id.");

  const payload = {
    full_name: safeText(fullName) || null,
    updated_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from("admin_users")
    .update(payload)
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .single();

  if (error) throw error;
  if (safeText(payload.full_name)) {
    writePendingAdminName(payload.full_name);
    clearPendingAdminName();
  }
  broadcastAdminProfileUpdated();
  return data;
}

export async function updateAdminThemePreference({ userId, themePreference } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Admin account is missing a user id.");

  const normalizedTheme = normalizeThemePreference(themePreference);
  if (!normalizedTheme) throw new Error("Admin theme preference is invalid.");

  const { data, error } = await supabase
    .from("admin_users")
    .update({
      theme_preference: normalizedTheme,
      updated_at: new Date().toISOString(),
    })
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .single();

  if (error) throw error;
  broadcastAdminProfileUpdated();
  return data;
}

export async function getAdminProfilePhotoUrl(path, expiresInSeconds = 3600) {
  const cleanPath = safeText(path);
  if (!cleanPath) return "";

  const { data, error } = await supabase.storage
    .from(ADMIN_PROFILE_BUCKET)
    .createSignedUrl(cleanPath, expiresInSeconds);

  if (error) {
    const message = safeText(error?.message).toLowerCase();
    if (
      message.includes("not found") ||
      message.includes("object not found") ||
      message.includes("no such object")
    ) {
      return "";
    }
    throw error;
  }
  return safeText(data?.signedUrl);
}

export async function uploadAdminProfilePhoto({ userId, file, previousPath = "" } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Admin account is missing a user id.");
  if (!(file instanceof File)) throw new Error("Please choose an image to upload.");

  const ext = fileExtensionFor(file);
  const nextPath = `admins/${normalizedUserId}/avatar-${Date.now()}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(ADMIN_PROFILE_BUCKET)
    .upload(nextPath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (uploadError) throw uploadError;

  const { data, error } = await supabase
    .from("admin_users")
    .update({
      profile_photo_path: nextPath,
      updated_at: new Date().toISOString(),
    })
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .single();

  if (error) throw error;

  const previous = safeText(previousPath);
  if (previous && previous !== nextPath) {
    await supabase.storage.from(ADMIN_PROFILE_BUCKET).remove([previous]);
  }

  broadcastAdminProfileUpdated();
  return data;
}

export async function removeAdminProfilePhoto({ userId, profilePhotoPath = "" } = {}) {
  const normalizedUserId = safeText(userId);
  if (!normalizedUserId) throw new Error("Admin account is missing a user id.");

  const existingPath = safeText(profilePhotoPath);
  const { data, error } = await supabase
    .from("admin_users")
    .update({
      profile_photo_path: null,
      updated_at: new Date().toISOString(),
    })
    .eq("user_id", normalizedUserId)
    .select("user_id,email,full_name,profile_photo_path,theme_preference,created_at,updated_at")
    .single();

  if (error) throw error;

  if (existingPath) {
    await supabase.storage.from(ADMIN_PROFILE_BUCKET).remove([existingPath]);
  }

  broadcastAdminProfileUpdated();
  return data;
}
